<template>
  <v-stepper class="fill-height" v-model="step">
    <v-stepper-header>
      <v-stepper-step :complete="step > 1" step="1"> 1. Wochen-Vorlage </v-stepper-step>
      <v-stepper-step :complete="step > 2" step="2"> 2. Spezialtage </v-stepper-step>
      <v-stepper-step :complete="step > 3" step="3"> 3. Mitarbeiter-Einstellungen </v-stepper-step>
      <v-stepper-step :complete="step > 4" step="4"> 4. Automatische Zuweisung </v-stepper-step>
      <v-stepper-step step="5"> 5. Einsatzplan-Übersicht </v-stepper-step>
    </v-stepper-header>

    <v-stepper-items class="fill-height" style="overflow: auto; padding-bottom: 250px">
      <v-stepper-content step="1">
        <v-container fluid>
          <v-row v-if="availableStudios.length > 1" class="mb-4 px-4">
            <v-col cols="12" md="4">
              <v-select
                filled
                dense
                hide-details
                :items="availableStudios"
                label="Studio für Vorlage wählen"
                v-model="selectedStudio"
                item-text="name"
                item-value="_id"
                @change="handleStudioChange"></v-select>
            </v-col>
          </v-row>
          <v-row v-else-if="availableStudios.length === 1 && !selectedStudio">
            {{ autoSelectStudio() }}
          </v-row>

          <weekly-template-planner
            v-if="!loading && selectedStudio && settings.shifts && settings.shifts.length > 0"
            :studio-id="selectedStudio"
            :available-shifts="settings.shifts"
            :key="`weekly-planner-${selectedStudio}`"
            @template-saved="handleTemplateUpdate" />
          <v-container v-else-if="loading" class="text-center fill-height d-flex justify-center align-center flex-column">
            <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
            <p class="mt-4 grey--text">Lade initiale Daten...</p>
          </v-container>
          <v-container v-else class="text-center mt-5">
            <p v-if="!selectedStudio && availableStudios.length > 0">Bitte wählen Sie ein Studio aus.</p>
            <p v-else-if="availableStudios.length === 0">Keine Studios zum Planen verfügbar.</p>
            <p v-else-if="!settings.shifts || settings.shifts.length === 0">
              Keine Schichtvorlagen gefunden. Bitte definieren Sie zuerst Schichten in den Einstellungen.
            </p>
            <p v-else>Lade Vorlagendaten...</p>
          </v-container>
        </v-container>
      </v-stepper-content>

      <v-stepper-content step="2">
        <v-container fluid>
          <v-row>
            <v-col cols="12" md="4">
              <v-card-title class="pl-0">Planungszeitraum</v-card-title>
              <v-menu v-model="dateMenu" :close-on-content-click="false" transition="scale-transition" offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    :value="dateRangeText"
                    label="Zeitraum auswählen"
                    prepend-icon="mdi-calendar"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    outlined
                    dense></v-text-field>
                </template>
                <v-date-picker v-model="planningDateRange" range no-title scrollable @input="dateMenu = false"></v-date-picker>
              </v-menu>
            </v-col>
          </v-row>

          <v-divider class="mb-4"></v-divider>

          <ShiftManagementMainDialogCreateSpecialDays
            v-if="step === 2 && selectedStudio"
            :studio-id="selectedStudio"
            :planning-start-date="planningDateRange[0]"
            :planning-end-date="planningDateRange[1]"
            :available-shifts="settings.shifts"
            :employees="employeesBasic"
            :weekly-template-data="loadedWeeklyTemplate"
            @back="step = 1"
            @next="step = 3" />
        </v-container>
      </v-stepper-content>

      <v-stepper-content step="3">
        <shift-management-employee-settings
          v-if="step === 3 && selectedStudio && planningDateRange.length === 2"
          :studio-id="selectedStudio"
          :planning-start-date="planningDateRange[0]"
          :planning-end-date="planningDateRange[1]"
          :weekly-template-data="loadedWeeklyTemplate"
          :available-shifts="settings.shifts"
          :school-holidays="settings.schoolHolidays || []"
          :confirmed-employee-settings="employeesBasic"
          ref="employeeSettings"
          @next="nextStep" />
        <v-container v-else class="text-center pa-5">
          <p>Bitte wählen Sie in Schritt 1 ein Studio und in Schritt 2 einen gültigen Planungszeitraum aus.</p>
          <v-btn text color="primary" @click="step = 1">Zurück zur Konfiguration</v-btn>
        </v-container>
      </v-stepper-content>

      <v-stepper-content step="4">
        <shift-management-assignment
          v-if="step === 4"
          :studio-id="selectedStudio"
          :planning-start-date="planningDateRange[0]"
          :planning-end-date="planningDateRange[1]"
          :loading="assigning"
          :finished="assignmentFinished"
          @trigger-assignment="triggerAssignment"
          @view-plan="step = 5"
          ref="assignmentComponent" />
      </v-stepper-content>

      <v-stepper-content step="5">
        <shift-management-roster-view
          v-if="step === 5 && assignedPlanData.length > 0 && assignedEmployees.length > 0"
          :plan-data="assignedPlanData"
          :studioId="selectedStudio"
          :availableShifts="settings.shifts"
          :unassignedShifts="unassignedShifts"
          :employees="assignedEmployees"
          :start-date="planningDateRange[0]"
          :school-holidays="settings.schoolHolidays || []"
          :loading="assigning"
          @refresh="refreshData" />
        <div v-else-if="step === 5" class="text-center pa-5">
          <p v-if="!assigning">
            Es konnten keine Zuweisungsdaten gefunden werden. Bitte führen Sie die automatische Zuweisung in Schritt 4 erneut aus.
          </p>
          <p v-else>Plan wird noch geladen...</p>
          <v-btn color="primary" @click="step = 4">Zurück zu Schritt 4</v-btn>
        </div>
      </v-stepper-content>
    </v-stepper-items>

    <v-card-actions style="position: fixed; bottom: 0; width: 100vw; justify-content: right; background: #121212; z-index: 5">
      <v-btn color="secondary" @click="prevStep" :disabled="step === 1 || assigning" v-if="step !== 2">Zurück</v-btn>

      <v-btn color="primary" @click="nextStep" class="mr-7" :disabled="step === 5 || step === 4" v-if="step !== 2">Weiter</v-btn>
    </v-card-actions>

    <v-dialog v-model="showConfirmationDialog" max-width="500px" persistent>
      <v-card>
        <v-card-title class="warning--text text--darken-2">
          <v-icon left color="warning">mdi-alert-circle-outline</v-icon>
          Achtung: Nicht alle Mitarbeiter bereit!
        </v-card-title>
        <v-card-text>
          <span v-if="confirmationDialogData.notReadyNames.length > 0">
            Folgende Mitarbeiter wurden nicht als "Bereit für Planung" markiert und werden bei der automatischen Zuweisung ignoriert:
            <ul>
              <li v-for="name in confirmationDialogData.notReadyNames" :key="name">{{ name }}</li>
            </ul>
          </span>
          <span v-else> Alle Mitarbeiter sind als "Bereit" markiert. </span>
          <br /><br />
          Möchten Sie mit den <strong>{{ confirmationDialogData.readyEmployeeIds.length }}</strong> als "Bereit" markierten Mitarbeitern fortfahren?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showConfirmationDialog = false">Abbrechen</v-btn>
          <v-btn color="warning" text @click="proceedToAssignment">Fortfahren</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-stepper>
</template>

<script>
import api from "@/api"
import WeeklyTemplatePlanner from "./WeeklyTemplatePlanner.vue"
import ShiftManagementEmployeeSettings from "./shiftManagementMainDialogCreateEmployee.vue"
import ShiftManagementAssignment from "./shiftManagementAssignment.vue"
import ShiftManagementRosterView from "./shiftManagementRosterView.vue"
import ShiftManagementMainDialogCreateSpecialDays from "./shiftManagementMainDialogCreateSpecialDays.vue" // <--- IMPORT NEU
import { mapState } from "vuex"

export default {
  name: "ArbeitsplanStepper",
  components: {
    WeeklyTemplatePlanner,
    ShiftManagementEmployeeSettings,
    ShiftManagementAssignment,
    ShiftManagementRosterView,
    ShiftManagementMainDialogCreateSpecialDays, // <--- COMPONENT REGISTRIEREN
  },
  data() {
    return {
      step: 1,
      loading: true,
      assigning: false,
      assignmentFinished: false,

      planningDateRange: [this.$moment().startOf("month").format("YYYY-MM-DD"), this.$moment().endOf("month").format("YYYY-MM-DD")],
      initialLoadComplete: false,
      dateMenu: false,

      selectedStudio: null,
      availableStudios: [],
      settings: { shifts: [] },

      loadedWeeklyTemplate: [],

      // NEU: Mitarbeiter-Liste für Schritt 2 (Special Days Dropdown)
      employeesBasic: [],

      assignedPlanData: [],
      assignedEmployees: [],
      unassignedShifts: [],
      showConfirmationDialog: false,
      confirmationDialogData: {
        notReadyNames: [],
        readyEmployeeIds: [],
      },
      confirmedEmployeeSettings: [],
    }
  },
  async created() {
    this.loading = true
    try {
      ;[this.settings, this.availableStudios] = await Promise.all([api.shiftManagement.fetchSettings("local"), api.fetchAllowedAdminStudios()])

      if (this.availableStudios.length > 0) {
        if (!this.selectedStudio) {
          this.selectedStudio = this.availableStudios[0]._id
        }
        await this.fetchWeeklyTemplate()
        // NEU: Mitarbeiter laden für Schritt 2
        await this.fetchBasicEmployees()
      }
    } catch (error) {
      this.$toast.error("Fehler beim Initialisieren der Daten.")
      console.error("Initialization Error:", error)
    } finally {
      this.loading = false
      this.initialLoadComplete = true
    }
  },
  computed: {
    ...mapState("studioStore", ["storeStudios"]),
    dateRangeText() {
      if (!this.planningDateRange || this.planningDateRange.length < 2) return ""
      const [start, end] = [...this.planningDateRange].sort()
      return `${this.$moment(start).format("DD.MM.YY")} - ${this.$moment(end).format("DD.MM.YY")}`
    },
  },
  methods: {
    async refreshData() {
      if (!this.selectedStudio) return

      this.assigning = true

      try {
        const response = await api.shiftManagement.fetchPlan(this.selectedStudio, this.planningDateRange[0], this.planningDateRange[1])

        // FIX: Sicherer Zugriff auf die Daten
        // 1. Prüfen: Ist 'response.data' vorhanden? Wenn ja, nutze es. Wenn nein, ist 'response' selbst die Daten.
        const responseData = response && response.data ? response.data : response

        // 2. Prüfen: Gibt es darin 'shifts'? Sonst ist das Objekt selbst das Array.
        const freshShifts = responseData && responseData.shifts ? responseData.shifts : Array.isArray(responseData) ? responseData : []

        if (!freshShifts) throw new Error("Keine Schicht-Daten empfangen")

        await this.handleAssignmentComplete(freshShifts, false)
        console.log("Plan erfolgreich aktualisiert.")
      } catch (e) {
        console.error("Fehler beim Refresh:", e)
        this.$toast.error("Konnte Ansicht nicht aktualisieren.")
      } finally {
        this.assigning = false
      }
    },
    // NEU: Lädt Mitarbeiter für Schritt 2 Dropdowns
    async fetchBasicEmployees() {
      if (!this.selectedStudio) return
      try {
        const emps = await api.shiftManagement.fetchShiftManagementEmployee()
        this.employeesBasic = emps.filter((e) => {
          const studioInfo = e.studio?.find((s) => String(s._id) === String(this.selectedStudio))
          return !!studioInfo
        })
      } catch (e) {
        console.error("Konnte Employees nicht laden", e)
      }
    },

    // ... (restliche Methoden bleiben gleich wie in deinem File) ...

    calculatePaidVacationHours(employeeData, planningSettings) {
      if (!this.planningDateRange || this.planningDateRange.length < 2) return 0

      const start = this.$moment(this.planningDateRange[0])
      const end = this.$moment(this.planningDateRange[1])
      const studioInfo = employeeData.studio.find((s) => s._id === this.selectedStudio)

      if (!studioInfo) return 0

      const percentage = this.getPlanningPercentage(employeeData, studioInfo, planningSettings)
      const dailyHours = 8.4 * (percentage / 100)

      let vacationHours = 0

      const vacations = employeeData.vacations || []

      vacations.forEach((vac) => {
        ;(vac.dates || []).forEach((d) => {
          const dateMoment = this.$moment(d.date)
          if (dateMoment.isBetween(start, end, null, "[]")) {
            const dayOfWeekISO = dateMoment.isoWeekday()
            if (dayOfWeekISO <= 5) {
              vacationHours += dailyHours
            }
          }
        })
      })

      return vacationHours
    },
    getPlanningPercentage(employee, studioInfo, planningSettings) {
      if (!studioInfo && !employee.timeTrackerPercentage) return 0
      const planningPercentages = planningSettings?.percentages
      if (planningPercentages && planningPercentages[studioInfo._id] != null) {
        return planningPercentages[studioInfo._id]
      }
      if (employee.timeTrackerPercentage != null) {
        return employee.timeTrackerPercentage
      }
      if (studioInfo) {
        return studioInfo.percentage
      }
      return 0
    },
    async handleStudioChange(newStudioId) {
      if (!newStudioId || !this.initialLoadComplete) return
      this.loading = true
      await this.fetchWeeklyTemplate()
      await this.fetchBasicEmployees() // NEU: Auch beim Studio Wechsel laden
      this.loading = false
    },
    async fetchWeeklyTemplate() {
      if (!this.selectedStudio) {
        this.loadedWeeklyTemplate = []
        return
      }
      this.loading = true
      try {
        this.loadedWeeklyTemplate = await api.shiftManagement.fetchWeeklyTemplate(this.selectedStudio)
      } catch (error) {
        this.$toast.error("Wochen-Vorlage konnte nicht geladen werden.")
        this.loadedWeeklyTemplate = []
      } finally {
        this.loading = false
      }
    },
    handleTemplateUpdate(savedTemplate) {
      this.loadedWeeklyTemplate = savedTemplate
      this.$toast.success("Vorlage im Hauptdialog aktualisiert.")
    },

    async triggerAssignment() {
      if (!this.confirmationDialogData.readyEmployeeIds || this.confirmationDialogData.readyEmployeeIds.length === 0) {
        this.$toast.error("Keine Mitarbeiter ausgewählt.")
        return
      }
      if (!this.confirmedEmployeeSettings || this.confirmedEmployeeSettings.length === 0) {
        this.$toast.error("Mitarbeiterdaten nicht gefunden.")
        return
      }

      this.assigning = true
      this.assignmentFinished = false

      const planningSettingsPayload = {}
      const [startDate, endDate] = this.planningDateRange

      try {
        const readyEmployeesData = this.confirmedEmployeeSettings.filter((emp) => this.confirmationDialogData.readyEmployeeIds.includes(emp.user))
        readyEmployeesData.forEach((employee) => {
          const settingsKey = `planningSettings_${employee._id}_${this.selectedStudio}`
          const savedSettings = JSON.parse(localStorage.getItem(settingsKey)) || {}
          const existing = employee.planningSettings || {}
          planningSettingsPayload[employee._id.toString()] = {
            percentages: savedSettings.percentages || {},
            recurringAbsence: savedSettings.recurringAbsence || [],
            ignore: savedSettings.hasOwnProperty("isReadyForPlanning") ? !savedSettings.isReadyForPlanning : existing.ignore || false,
            fixedShifts: savedSettings.fixedShifts || existing.fixedShifts || {},
            longCommute: savedSettings.longCommute || existing.longCommute || false,
          }
        })

        // 1. GENERIEREN
        console.log("Schritt 4a: Generiere Schichten...")
        const generationResponse = await api.shiftManagement.generatePlanFromTemplate(this.selectedStudio, startDate, endDate)
        console.log(`[GENERATE] ${generationResponse.createdCount} erstellt.`)

        // 2. ZUWEISEN
        console.log("Schritt 4b: Weise Mitarbeiter zu...")
        const updatedPlan = await api.shiftManagement.assignEmployeesToPlan(
          this.selectedStudio,
          startDate,
          endDate,
          planningSettingsPayload,
          this.confirmationDialogData.readyEmployeeIds,
        )

        if (!updatedPlan || updatedPlan.length === 0) {
          throw new Error("Leerer Plan zurückgegeben.")
        }

        // 3. ERFOLG
        await this.handleAssignmentComplete(updatedPlan, false)
        this.assignmentFinished = true
      } catch (error) {
        this.$toast.error("Fehler im Zuweisungsprozess.")
        console.error("Assignment Error:", error)
        this.handleAssignmentComplete([], false)
      } finally {
        this.assigning = false
      }
    },
    async handleAssignmentComplete(updatedPlan, switchStep = true) {
      console.group("🔍 DEBUG: handleAssignmentComplete")
      console.log("1. Plan erhalten mit", updatedPlan.length, "Einträgen")

      this.assignedPlanData = updatedPlan.filter((p) => p.assignedEmployee)
      this.unassignedShifts = updatedPlan.filter((p) => !p.assignedEmployee)
      console.log("2. Davon zugewiesen:", this.assignedPlanData.length)

      if (switchStep) this.assigning = true

      try {
        const allStudioEmployees = await api.shiftManagement.fetchShiftManagementEmployee()
        let sourceEmployees = this.confirmedEmployeeSettings
        if (!sourceEmployees || sourceEmployees.length === 0) {
          sourceEmployees = allStudioEmployees.filter((emp) => emp.studio && emp.studio.some((s) => String(s._id) === String(this.selectedStudio)))
        }

        const employeeMap = new Map(
          sourceEmployees.map((e) => {
            const uId = String(e.user._id || e.user)
            return [uId, e]
          }),
        )

        const assignedUserIds = [...new Set(this.assignedPlanData.map((p) => String(p.assignedEmployee._id || p.assignedEmployee)))]

        this.assignedEmployees = assignedUserIds
          .map((userId) => {
            let employeeData = employeeMap.get(userId)
            if (!employeeData) {
              employeeData = allStudioEmployees.find((e) => String(e.user._id || e.user) === userId)
            }

            if (!employeeData) {
              return null
            }

            const settingsKey = `planningSettings_${employeeData._id}_${this.selectedStudio}`
            const savedSettings = JSON.parse(localStorage.getItem(settingsKey)) || {}

            const planningSettings = {
              percentages: savedSettings.percentages || employeeData.planningSettings?.percentages || {},
              recurringAbsence: savedSettings.recurringAbsence || employeeData.planningSettings?.recurringAbsence || [],
              isReadyForPlanning: savedSettings.isReadyForPlanning || employeeData.planningSettings?.isReadyForPlanning || false,
            }

            if (employeeData.fullName === "Daniela Lombardi") {
              console.log("DEBUG: Berechne Daten für Daniela", {
                employeeData,
                savedSettings,
                planningSettings,
                sollStunden: this.calculateSollStunden(employeeData, planningSettings),
                plannedHours: this.calculatePlanned,
              })
            }
            return {
              ...employeeData,
              _id: userId,
              sollStunden: this.calculateSollStunden(employeeData, planningSettings),
              plannedHours:
                this.calculatePlannedHours(userId, this.assignedPlanData) + this.calculatePaidVacationHours(employeeData, planningSettings),
              planningSettings: planningSettings,
            }
          })
          .filter((e) => e !== null)

        console.groupEnd()
      } catch (error) {
        console.error("Roster Prep Error:", error)
        this.$toast.error("Fehler beim Aufbereiten der Ansicht.")
        this.assignedEmployees = []
        console.groupEnd()
      } finally {
        if (switchStep) {
          this.assigning = false
          this.step = 5
        }
      }
    },
    calculateSollStunden(employeeData, planningSettings) {
      if (!this.planningDateRange || this.planningDateRange.length < 2 || !employeeData) return 0
      const start = this.$moment(this.planningDateRange[0])
      const end = this.$moment(this.planningDateRange[1])
      let businessDays = 0
      let current = start.clone()

      const studioInfo = employeeData.studio.find((s) => s._id === this.selectedStudio)
      if (!studioInfo) return 0

      const percentage = this.getPlanningPercentage(employeeData, studioInfo, planningSettings)

      while (current.isSameOrBefore(end, "day")) {
        const dayOfWeekISO = current.isoWeekday()
        const isWeekend = dayOfWeekISO >= 6
        const isSchoolHoliday = (this.settings.schoolHolidays || []).some(
          (sh) => sh.startDate && sh.endDate && current.isBetween(sh.startDate, sh.endDate, null, "[]"),
        )

        let isSchoolDay = false
        if (employeeData.isApprentice && !isSchoolHoliday) {
          isSchoolDay = (employeeData.schoolDays || []).some((sd) => sd.dayOfWeek === dayOfWeekISO)
        }

        let isWorkDay = !isWeekend && !isSchoolDay
        if (employeeData.isApprentice && isSchoolHoliday) {
          isWorkDay = false
        }

        if (isWorkDay) {
          businessDays++
        }
        current.add(1, "day")
      }
      return businessDays * (8.4 * (percentage / 100))
    },
    calculatePlannedHours(userId, planData) {
      let totalHours = 0
      planData.forEach((plan) => {
        const planUserId = String(plan.assignedEmployee._id || plan.assignedEmployee)
        if (planUserId === String(userId)) {
          const timesToUse = plan.times && plan.times.length > 0 ? plan.times : plan.shiftTemplate ? plan.shiftTemplate.times : []
          if (timesToUse) {
            timesToUse.forEach((time) => {
              if (time.type === "Arbeitszeit" && time.start && time.end) {
                const startTime = this.$moment(time.start, "HH:mm")
                const endTime = this.$moment(time.end, "HH:mm")
                if (endTime.isBefore(startTime)) {
                  endTime.add(1, "day")
                }
                totalHours += endTime.diff(startTime, "hours", true)
              }
            })
          }
        }
      })
      return totalHours
    },

    nextStep() {
      if (this.step === 3) {
        if (this.$refs.employeeSettings) {
          const status = this.$refs.employeeSettings.getConfirmationStatus()
          this.confirmationDialogData.notReadyNames = status.notReadyNames
          this.confirmationDialogData.readyEmployeeIds = status.readyEmployeeIds
          this.confirmedEmployeeSettings = this.$refs.employeeSettings.employees || []

          if (!status.allMarked) {
            this.showConfirmationDialog = true
            return
          }
        }
      }
      if (this.step < 5) {
        this.step++
      }
    },
    prevStep() {
      if (this.step > 1) this.step--
    },
    proceedToAssignment() {
      this.showConfirmationDialog = false
      this.step = 4
    },
    autoSelectStudio() {
      if (this.availableStudios.length === 1 && !this.selectedStudio) {
        this.$nextTick(() => {
          this.selectedStudio = this.availableStudios[0]._id
        })
      }
      return ""
    },
  },
  watch: {
    selectedStudio(newStudio, oldStudio) {
      if (this.initialLoadComplete && newStudio !== oldStudio) {
        this.fetchWeeklyTemplate()
      }
    },
    step(newStep, oldStep) {
      if (this.initialLoadComplete && newStep === 1 && oldStep > 1) {
        this.fetchWeeklyTemplate()
      }
      if (newStep === 3 && this.loadedWeeklyTemplate.length === 0 && !this.loadingTemplate) {
        this.fetchWeeklyTemplate()
      }
      // Reset Assignment Status beim Zurückgehen
      if (newStep === 4) {
        this.assignmentFinished = false
      }
    },
  },
}
</script>
